/**
 * Memory Technique Utilities
 * Implements the Major System for converting numbers to memorable pictures/words
 */

// Major System: Digit to consonant sound mapping
const DIGIT_TO_CHARACTER: Record<number, string> = {
  0: 's/z',
  1: 't/d',
  2: 'n',
  3: 'm',
  4: 'r',
  5: 'l',
  6: 'j/ch/sh',
  7: 'k/g',
  8: 'f/v',
  9: 'p/b'
};

// Major System: Number (0-99) to picture/word mapping
// Based on combining the consonant sounds of the two digits
const NUMBER_TO_PICTURE: Record<number, string> = {
  // 00-09
  0: 'sauce', 1: 'tie', 2: 'knee', 3: 'mow', 4: 'row', 5: 'law', 6: 'jaw', 7: 'key', 8: 'fee', 9: 'pie',
  // 10-19
  10: 'toes', 11: 'toad', 12: 'tuna', 13: 'tomb', 14: 'tire', 15: 'tale', 16: 'tissue', 17: 'tack', 18: 'tuff', 19: 'tub',
  // 20-29
  20: 'nose', 21: 'net', 22: 'nun', 23: 'name', 24: 'nerd', 25: 'nail', 26: 'niche', 27: 'neck', 28: 'knife', 29: 'nap',
  // 30-39
  30: 'maze', 31: 'mat', 32: 'moon', 33: 'mime', 34: 'moor', 35: 'mail', 36: 'match', 37: 'mug', 38: 'muff', 39: 'map',
  // 40-49
  40: 'rose', 41: 'rat', 42: 'rain', 43: 'ram', 44: 'rear', 45: 'rail', 46: 'rash', 47: 'rag', 48: 'roof', 49: 'rope',
  // 50-59
  50: 'lace', 51: 'lad', 52: 'lion', 53: 'lamb', 54: 'lure', 55: 'lily', 56: 'leech', 57: 'log', 58: 'leaf', 59: 'lap',
  // 60-69
  60: 'chase', 61: 'cheat', 62: 'chain', 63: 'chime', 64: 'chair', 65: 'chill', 66: 'choo-choo', 67: 'chalk', 68: 'chef', 69: 'chip',
  // 70-79
  70: 'case', 71: 'cat', 72: 'coin', 73: 'comb', 74: 'car', 75: 'coal', 76: 'cage', 77: 'cook', 78: 'cave', 79: 'cap',
  // 80-89
  80: 'face', 81: 'fat', 82: 'fan', 83: 'foam', 84: 'fire', 85: 'file', 86: 'fish', 87: 'fog', 88: 'fife', 89: 'fob',
  // 90-99
  90: 'pace', 91: 'pat', 92: 'pan', 93: 'palm', 94: 'pear', 95: 'pail', 96: 'peach', 97: 'pack', 98: 'puff', 99: 'pope'
};

/**
 * Get the Major System character mapping for a digit (0-9)
 */
export function getCharacterMapping(digit: number): string {
  if (digit < 0 || digit > 9) {
    return '';
  }
  return DIGIT_TO_CHARACTER[digit] || '';
}

/**
 * Get all character mappings (0-9) for reference display
 */
export function getAllCharacterMappings(): Array<{ digit: number; character: string }> {
  return Array.from({ length: 10 }, (_, i) => ({
    digit: i,
    character: DIGIT_TO_CHARACTER[i] || ''
  }));
}

/**
 * Get the picture/word for a number (0-99) based on Major System
 */
export function getPictureForNumber(num: number): string {
  if (num < 0 || num > 99) {
    return '';
  }
  return NUMBER_TO_PICTURE[num] || '';
}

/**
 * Break down a number into 2-digit pairs (right-to-left)
 * Handles odd-length numbers by padding with 0 on the left
 * Example: 12345 -> [12, 34, 5] -> [12, 34, 5] (but we want [12, 34, 5] -> actually [12, 34, 5])
 * Actually, for memory techniques, we typically go right-to-left in pairs
 * Example: 12345 -> [45, 23, 1] but we pad to [45, 23, 01]
 * Let's do it properly: 12345 -> pairs from right: 45, 23, 1 -> pad to [45, 23, 01]
 */
export function breakdownNumber(numberStr: string): number[] {
  // Remove any non-digit characters
  const digits = numberStr.replace(/\D/g, '');
  
  if (!digits) {
    return [];
  }
  
  // Convert to array of digits
  const digitArray = digits.split('').map(Number);
  
  // If odd number of digits, pad with 0 on the left
  if (digitArray.length % 2 !== 0) {
    digitArray.unshift(0);
  }
  
  // Group into pairs from left to right
  const pairs: number[] = [];
  for (let i = 0; i < digitArray.length; i += 2) {
    const pair = digitArray[i] * 10 + digitArray[i + 1];
    pairs.push(pair);
  }
  
  return pairs;
}

/**
 * Get picture associations for a number breakdown
 */
export function getPictureAssociations(pairs: number[]): Array<{ number: number; picture: string }> {
  return pairs.map(num => ({
    number: num,
    picture: getPictureForNumber(num)
  }));
}

